// src/contexts/PageContext.tsx
// Context for pages to set global header band content (headline, title, breadcrumbs, tabs)
// Pro pages typically set "headline" (big welcome), Freemium pages set "title" (smaller)

import React, { createContext, useContext, useMemo, useState, useCallback } from "react";

interface Breadcrumb {
  label: string;
  href?: string;
}

type PageCtx = {
  breadcrumbs?: Breadcrumb[];  // e.g., [{ label: "Home", href: "/home" }, { label: "Snapshot" }]
  title?: string;              // small subtitle (Freemium)
  headline?: string;           // big welcome line (Pro)
  tabs?: string[];             // ["Tools", "Documents", "Messages"]
  activeTab?: string;          // which tab is active
  rightSlot?: React.ReactNode; // optional right-side actions in the context band (replaces tabs when present)
};

type PageCtxValue = {
  page: PageCtx;
  setPageContext: (p: Partial<PageCtx>) => void;
};

const Ctx = createContext<PageCtxValue | undefined>(undefined);

export function PageContextProvider({ children }: { children: React.ReactNode }) {
  const [page, setPage] = useState<PageCtx>({
    breadcrumbs: [{ label: "Home" }],
    title: "",
    headline: "",
    tabs: [],
    activeTab: undefined,
  });

  // ✅ FIX: Use useCallback to stabilize setPageContext function
  const setPageContext = useCallback((p: Partial<PageCtx>) => {
    setPage(prev => ({ ...prev, ...p }));
  }, []); // Empty deps - setPage from useState is stable

  const value = useMemo(
    () => ({
      page,
      setPageContext,
    }),
    [page, setPageContext] // Now setPageContext is stable
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function usePageContext() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("usePageContext must be used within PageContextProvider");
  return ctx;
}